// @flow
import { Button } from 'reactstrap';
import React, { useState } from 'react';
import { camelizeKeys } from 'humps';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import { useEffectOnFirstRender } from '../../../hooks';

import { useFlightReviews } from '../Common/FlightReviewsProvider';

import HimalayanBankForm from './HimalayanBankForm';
import { injectFlightSearchParams, FlightSession } from '../Common';
import { Promocode, Prices } from '../../../components';

import { trackHGCreditApplySuccess } from '../../../redux/actions/analyticsEventActions';

import api from '../../../lib/api';
import endpoints from '../../../lib/endpoints';

import { getAdditionalCost } from './utils';
import { roundOff2 } from '../../../lib/utils';

import { HBL_FORM_ID } from './HimalayanBankForm';

const PAYMENT_GATEWAY = "hbl";
const GATEWAY_FEE_NAME = "hbl-transaction-fee";

type StructuredData = {
  amount: string,
  hashValue: string,
  invoiceNo: string,
  nonSecure: string,
  gatewayId: string,
  invoiceId: string,
  gatewayUrl: string,
  currencyCode: string,
};

const getInvoicesIdFromUrl = (url) => Number(url.split('/flights/details-review/')[1]);

function SubmitButton(props) {
  const {
    outbound,
  } = useFlightReviews();

  const invoiceId = getInvoicesIdFromUrl(props.location.pathname);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [promocodeResponse, setPromocodeResponse] = useState(null);
  const [verifiedAmounts, setVerifiedAmounts] = useState({
    additionalCost: null,
    purchaseAmount: null,
    totalAmount: null,
  });
  const { additionalCost, purchaseAmount, totalAmount } = verifiedAmounts;
  let discount = promocodeResponse ? promocodeResponse.promocodeAmount : 0;

  useEffectOnFirstRender(fetchInvoices);
  
  async function fetchInvoices() {
      // VERIFY INVOICE AND STORE AMOUNTS
      const { data: verifiedAmounts } = await api.postWithoutToken(endpoints.payment.verify, {
        invoice_id: invoiceId,
        additional_costs: { payment_gateway: PAYMENT_GATEWAY },
      });
      const camelizedAmounts = camelizeKeys(verifiedAmounts);
      const additionalCost = getAdditionalCost(camelizedAmounts.data, GATEWAY_FEE_NAME);
      const {
        totalAmount,
        purchaseAmount,
      } = camelizedAmounts;

      setVerifiedAmounts({
        totalAmount,
        purchaseAmount,
        additionalCost,
      });
  }

  async function createOrder() {
    setIsLoading(true);

    const reqBody = promocodeResponse ? { promocode: promocodeResponse.promocode } : {};

    try {
      const { data } = await api.postWithoutToken(endpoints.flights.createHBLPaymentOrder(invoiceId),reqBody);
      setPaymentDetails(restructurePaymentData(data));
    } catch(e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }
  
  function handlePromocodeApplySuccess(res) {
    const { additionalCost, purchaseAmount, totalAmount } = res;
    const { trackHGCreditApplySuccess } = props;

    setPromocodeResponse(res);
    setVerifiedAmounts({
      additionalCost,
      purchaseAmount,
      totalAmount,
      withoutPromocode: verifiedAmounts,
    });

    trackHGCreditApplySuccess();
  }
  
  function handlePromocodeRemove() {
    setPromocodeResponse(null);

    setVerifiedAmounts({
      ...verifiedAmounts.withoutPromocode,
    });
  }

  async function handleContinueToPaymentClick() {
    if (FlightSession.hasExpired()) {
      window.location.reload();
      return;
    };

    await createOrder();
    // Wait for Form in the page to update with Payment details from createOrder before submitting.
    // Submitting HBL Form Redirects the Page to the Payment Gateway
    setTimeout(submitHBLForm, 500);
  }

  function submitHBLForm() {
    const hblForm = document.getElementById(HBL_FORM_ID);
    if (hblForm) {
      FlightSession.end();
      hblForm.submit();
    }
  }
    
  const {
    amount,
    hashValue,
    invoiceNo,
    nonSecure,
    gatewayId,
    gatewayUrl,
    currencyCode,
  } = paymentDetails;

  return (
    <div className="container">
      <div className="row flight-summary-foot">
        <div className="col-lg-8 col-md-8 col-12"></div>
        <div className="col-12 col-lg-4 col-md-4 text-right atc">
          <Prices
            discount={discount}
            basePrice={purchaseAmount}
            grandTotal={totalAmount}
            currency={outbound.currency}
            additionalCost={additionalCost}
            purchaseAmount={roundOff2(purchaseAmount - discount)}
          />
          <Button
            color="yellow"
            onClick={handleContinueToPaymentClick}
            id="flight-confirm-payment"
            className="mb-2"
          >
            {isLoading ? 'Processing...' : 'CONTINUE TO PAYMENT'}
          </Button>
        <Promocode
          invoiceId={invoiceId}
          onDelete={handlePromocodeRemove}
          onSuccess={handlePromocodeApplySuccess}
          isPromocodeApplied={promocodeResponse && promocodeResponse}
          paymentGateway={PAYMENT_GATEWAY}
          transactionFeeName={GATEWAY_FEE_NAME}
        />
        </div>
        <HimalayanBankForm
          amount={amount}
          amountInUSD={totalAmount}
          hblUrl={gatewayUrl}
          invoiceNo={invoiceNo}
          invoiceId={invoiceId}
          nonSecure={nonSecure}
          hashValue={hashValue}
          currencyCode={currencyCode}
          hblPaymentGatewayId={gatewayId}
        />
      </div>
    </div>
  );
}

function restructurePaymentData(data): StructuredData {
  const {
    hash,
    amount,
    gateway,
    nonsecure,
    invoice_num,
    currency_code,
  } = data;

  return {
    amount,
    hashValue: hash,
    nonSecure: nonsecure,
    gatewayId: gateway.id,
    invoiceNo: invoice_num,
    gatewayUrl: gateway.url,
    currencyCode: currency_code,
  };
}

const mapStateToProps = (state) => ({
  sessionId: state.flights.sessionId,
});

const ConnectedSubmitButton = connect(mapStateToProps, { trackHGCreditApplySuccess })(SubmitButton);

export default withRouter(injectFlightSearchParams(ConnectedSubmitButton));



// WEBPACK FOOTER //
// ./src/screens/Flights/Review/HBLSubmitButton.js